// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

import './styles/main.css';

import { buildFindingsCsv, buildMappingPayload, buildReport } from './artefacts';
import type { AppliedFinding } from './core/apply';
import { DEFAULT_ETA_THRESHOLD } from './core/entropy';
import type { PseudonymEntry, ScanSettings } from './core/protocol';
import { COUNTS, RULES } from './core/rules';
import { sniff } from './core/sniff';
import type { RunState, Severity } from './core/types';
import { openMapping, peekMeta, sealMapping, type MappingMeta } from './crypto/container';
import { generatePassphrase, passphraseBits } from './crypto/passphrase';
import { formatProjectKey, keyFingerprint, newProjectSecret } from './crypto/projectkey';
import { EventLog } from './eventlog';
import { initGlossary } from './glossary';
import { copyText, downloadBlob, downloadBytes, downloadText } from './io/download';
import { BLOB_CAP_BYTES, chooseSink, describeSink, openSink, type Sink, type SinkKind } from './io/sink';
import { poolSize, runPass } from './pipeline';
import { createDrawer, createModalHost, el, formatBytes, formatCount, formatEta, toast } from './ui/shell';

const $ = <T extends HTMLElement>(id: string): T => document.getElementById(id) as T;

/* ── Capability gate ──────────────────────────────────────────────────────
   One app-level gate, no per-rule fallbacks. A silent fallback that changes
   which rules run is worse than a clear refusal. Lookbehind and the streams
   API both land at Safari 16.4, so there is exactly one floor to state. */
function unsupported(reason: string): void {
  document.body.replaceChildren(
    el('div', { style: 'max-width:44rem;margin:12vh auto;padding:0 1.5rem;font-family:system-ui' }, [
      el('h1', {}, ['Scrubjay cannot run in this browser']),
      el('p', {}, [
        `It needs ${reason}. The floor is Chrome 103, Firefox 114, Safari 16.4 or iOS 16.4 — and a secure context (https).`,
      ]),
      el('p', {}, ['Nothing was read from your device.']),
    ]),
  );
}

function checkCapabilities(): boolean {
  if (!self.isSecureContext || !crypto?.subtle) {
    unsupported('a secure context with Web Crypto');
    return false;
  }
  for (const k of ['Worker', 'TextDecoder', 'MessageChannel'] as const) {
    if (!(k in self)) {
      unsupported(k);
      return false;
    }
  }
  try {
    // eslint-disable-next-line prefer-regex-literals
    new RegExp('(?<=a)b');
  } catch {
    unsupported('regular-expression lookbehind');
    return false;
  }
  return true;
}

/* ── State ─────────────────────────────────────────────────────────────── */

type Phase = 'input' | 'preflight' | 'scanning' | 'triage' | 'export';

interface ClassAgg {
  rule: string;
  severity: Severity;
  tier: number;
  count: number;
  distinct: Set<string>;
  samples: AppliedFinding[];
}

const state = {
  phase: 'input' as Phase,
  file: null as File | null,
  enabled: new Set<string>(RULES.filter((r) => r.enabledByDefault).map((r) => r.id)),
  dismissed: new Set<string>(),
  dismissHistory: [] as string[],
  classes: new Map<string, ClassAgg>(),
  entries: new Map<string, PseudonymEntry>(),
  records: 0,
  malformed: 0,
  decompressedBytes: 0,
  runState: 'COMPLETE' as RunState,
  cursor: 0,
  expanded: new Set<string>(),
  projectSecret: newProjectSecret(),
  projectKey: '',
  keyFp: '',
  passphrase: generatePassphrase(),
  cleanBlob: null as Blob | null,
  cleanWritten: null as number | null,
  startedAt: new Date(),
  sinkKind: 'none' as SinkKind,
  truncated: false,
};

/** Bounded retention: exact counters always, plus the first N findings verbatim per class. */
const VERBATIM_PER_CLASS = 400;
const GLOBAL_FINDING_CAP = 200_000;
let bufferedFindings = 0;

const log = new EventLog($<HTMLOListElement>('drawer-list'));
const modal = createModalHost($('modal-host'));
let abort = new AbortController();

/* ── Phase switching ───────────────────────────────────────────────────── */

function show(id: string, on: boolean): void {
  const node = document.getElementById(id);
  if (!node) return;
  if (on) node.removeAttribute('hidden');
  else node.setAttribute('hidden', '');
}

function setPhase(p: Phase): void {
  state.phase = p;
  show('input-panel', p === 'input');
  show('preflight-panel', p === 'preflight');
  show('progress-panel', p === 'scanning');
  show('triage-panel', p === 'triage' || p === 'export');
  show('export-panel', p === 'export');
}

function fail(message: string): void {
  $('error-text').textContent = message;
  show('error', true);
  log.emit('error', message);
}

function clearError(): void {
  show('error', false);
}

/* ── Input ─────────────────────────────────────────────────────────────── */

async function acceptFile(file: File): Promise<void> {
  clearError();
  state.file = file;
  log.emit('info', `Selected ${file.name} (${formatBytes(file.size)}). Nothing has been uploaded.`);

  const head = new Uint8Array(await file.slice(0, 8).arrayBuffer());
  const s = sniff(head, file.name);

  const summary = $('file-summary');
  summary.textContent = `${file.name} — ${formatBytes(file.size)}`;
  summary.removeAttribute('hidden');

  const facts = $('preflight-facts');
  facts.replaceChildren();

  if (s.refuse) {
    facts.append(el('div', { class: 'callout is-bad' }, [s.refuse]));
    log.emit('warn', s.refuse);
    setPhase('preflight');
    ($('scan-start') as HTMLButtonElement).disabled = true;
    return;
  }
  ($('scan-start') as HTMLButtonElement).disabled = false;

  const wantClean = ($('opt-clean') as HTMLInputElement).checked;
  state.sinkKind = chooseSink(file.size, wantClean);

  facts.append(
    el('div', { class: 'callout' }, [
      el('strong', {}, ['Format: ']),
      `${s.codec === 'gzip' ? 'gzip-compressed text' : s.codec === 'parquet' ? 'Parquet' : 'plain text'} — detected from ${s.how}.`,
    ]),
  );

  if (s.codec === 'parquet') {
    facts.append(
      el('div', { class: 'callout is-warn' }, [
        el('strong', {}, ['Parquet input produces JSONL output. ']),
        'There is no Parquet writer in this version. Parquet is read one ',
        el('button', { type: 'button', class: 'glossary-link', 'data-term': 'row-group' }, ['row group']),
        ' at a time, so the honest memory ceiling on this path is the size of the largest row group, not the size of the file.',
      ]),
    );
  }

  facts.append(el('div', { class: 'callout' }, [el('strong', {}, ['Output: ']), describeSink(state.sinkKind, file.size)]));
  $('sink-note').textContent =
    state.sinkKind === 'fsa'
      ? ' Streams straight to disk on this browser.'
      : state.sinkKind === 'blob'
        ? ` Built in memory here, capped at ${formatBytes(BLOB_CAP_BYTES)}.`
        : ' Not available for a file this size in this browser.';

  facts.append(
    el('div', { class: 'callout' }, [
      el('strong', {}, ['Two passes. ']),
      'One to find things, one to rewrite them, so you can change your mind in between. Time depends on your machine — the estimate is measured while it runs, never guessed up front.',
    ]),
  );

  renderRuleList();
  setPhase('preflight');
}

function renderRuleList(): void {
  const list = $<HTMLUListElement>('rule-list');
  list.replaceChildren();
  for (const r of RULES) {
    const cb = el('input', { type: 'checkbox' }) as HTMLInputElement;
    cb.checked = state.enabled.has(r.id);
    cb.addEventListener('change', () => {
      if (cb.checked) state.enabled.add(r.id);
      else state.enabled.delete(r.id);
      log.emit('info', `${r.id} ${cb.checked ? 'enabled' : 'disabled'}.`);
    });
    const label = el('label', {}, [
      cb,
      el('span', {}, [
        el('span', { class: 'rule-name' }, [`${r.label} `]),
        el('span', { class: 'class-kind' }, [`${r.id} · ${r.severity}${r.tier === 3 ? ' · heuristic' : ''}`]),
        el('span', { class: 'rule-not' }, [`Does not match: ${r.doesNotMatch}`]),
      ]),
    ]);
    list.append(el('li', { class: 'rule-item' }, [label]));
  }
}

/* ── Scanning ──────────────────────────────────────────────────────────── */

function settings(): ScanSettings {
  return {
    enabledRules: [...state.enabled],
    entropyEta: DEFAULT_ETA_THRESHOLD,
    dismissedRules: [...state.dismissed],
    dismissedKeys: [],
    rewriteKeys: ($('opt-keys') as HTMLInputElement).checked,
    projectSecret: state.projectSecret,
    profile: 'SJ1',
    tokenChars: 10,
  };
}

function ingestFindings(found: AppliedFinding[]): void {
  for (const f of found) {
    if (f.suppressedBy !== '') continue;
    let agg = state.classes.get(f.rule);
    if (!agg) {
      agg = { rule: f.rule, severity: f.severity, tier: f.tier, count: 0, distinct: new Set(), samples: [] };
      state.classes.set(f.rule, agg);
    }
    agg.count++;
    if (agg.distinct.size < 50_000) agg.distinct.add(f.value);
    if (agg.samples.length < VERBATIM_PER_CLASS && bufferedFindings < GLOBAL_FINDING_CAP) {
      agg.samples.push(f);
      bufferedFindings++;
    } else if (bufferedFindings >= GLOBAL_FINDING_CAP) {
      state.truncated = true;
    }
  }
}

function ingestEntries(entries: PseudonymEntry[]): void {
  for (const e of entries) {
    const k = `${e.type} ${e.normalised}`;
    const prev = state.entries.get(k);
    if (prev) state.entries.set(k, { ...prev, count: prev.count + e.count });
    else state.entries.set(k, e);
  }
}

async function startScan(): Promise<void> {
  const file = state.file;
  if (!file) return;
  clearError();
  state.classes.clear();
  state.entries.clear();
  state.dismissed.clear();
  state.dismissHistory = [];
  state.truncated = false;
  bufferedFindings = 0;
  state.records = 0;
  state.malformed = 0;
  state.runState = 'COMPLETE';
  state.startedAt = new Date();
  abort = new AbortController();

  setPhase('scanning');
  $('progress-bar').style.width = '0%';
  log.emit('info', `Pass 1 of 2 — reading ${file.name} with ${poolSize()} scanner workers.`);

  try {
    const result = await runPass(
      { file, pass: 1, settings: settings(), sink: null, gzipOutput: false, signal: abort.signal },
      {
        onProgress: (p) => {
          const pct = p.compressedTotal > 0 ? (p.compressedRead / p.compressedTotal) * 100 : 0;
          $('progress-bar').style.width = `${Math.min(100, pct).toFixed(1)}%`;
          $('progress-meta').replaceChildren(
            el('span', {}, [`${formatBytes(p.compressedRead)} of ${formatBytes(p.compressedTotal)}`]),
            el('span', {}, [formatEta(p.compressedTotal - p.compressedRead, p.bytesPerSecond)]),
            el('span', {}, [`${formatCount(p.records)} records`]),
            el('span', {}, [`${formatCount(p.findings)} findings`]),
            el('span', {}, [`${p.workers} workers`]),
          );
        },
        onFindings: ingestFindings,
        onEntries: ingestEntries,
        onEvent: (level, message) => log.emit(level, message),
      },
    );
    state.records = result.records;
    state.malformed = result.malformed;
    state.decompressedBytes = result.decompressedBytes;
    if (result.cancelled) {
      state.runState = 'INCOMPLETE';
      log.emit('warn', 'Scan cancelled. The results below cover only what was read.');
    } else {
      log.emit('good', `Pass 1 complete — ${formatCount(result.records)} records.`);
    }
    if (result.malformed > 0) {
        log.emit('warn', `${plural(result.malformed, 'line')} were not valid JSON and were scanned as raw text.`);
    }
    renderTriage();
    setPhase('triage');
  } catch (e) {
    setPhase('preflight');
    fail(e instanceof Error ? e.message : String(e));
  }
}

/* ── Triage ────────────────────────────────────────────────────────────── */

const SEV_RANK: Record<Severity, number> = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };

/** `1 findings · 1 distinct values` reads like a placeholder, and undermines everything else. */
function plural(n: number, one: string, many = `${one}s`): string {
  return `${formatCount(n)} ${n === 1 ? one : many}`;
}

function orderedClasses(): ClassAgg[] {
  return [...state.classes.values()].sort(
    (a, b) => SEV_RANK[a.severity] - SEV_RANK[b.severity] || b.count - a.count || a.rule.localeCompare(b.rule),
  );
}

function totals(): { findings: number; active: number } {
  let findings = 0;
  let active = 0;
  for (const c of state.classes.values()) {
    findings += c.count;
    if (!state.dismissed.has(c.rule)) active += c.count;
  }
  return { findings, active };
}

function renderTriage(): void {
  const list = orderedClasses();
  const t = totals();

  const headline = $('headline');
  headline.replaceChildren();
  if (t.findings === 0) {
    headline.append(
      el('span', { class: 'big is-good' }, ['0']),
      el('span', {}, ['findings. Nothing matched — which is not the same as the file being clean.']),
    );
  } else {
    const critical = list.filter((c) => c.severity === 'critical').reduce((a, c) => a + c.count, 0);
    headline.append(
      el('span', { class: `big ${critical > 0 ? 'is-bad' : ''}` }, [formatCount(t.findings)]),
      el('span', {}, [
        `findings in ${formatCount(state.records)} records — ${formatCount(critical)} of them critical.`,
      ]),
    );
  }

  const stats = $('stats');
  stats.replaceChildren();
  const add = (k: string, v: string): void => {
    stats.append(el('div', {}, [el('dt', {}, [k]), el('dd', {}, [v])]));
  };
  add('Records', formatCount(state.records));
  add('Rule classes hit', formatCount(state.classes.size));
  add('Distinct entities', formatCount(state.entries.size));
  add('Read', formatBytes(state.decompressedBytes));
  if (state.malformed > 0) add('Unparseable lines', formatCount(state.malformed));

  $('triage-summary').textContent =
    state.dismissed.size === 0
      ? `${plural(t.active, 'finding')} will be redacted.`
      : `${formatCount(t.active)} will be redacted · ${formatCount(t.findings - t.active)} dismissed in ${plural(state.dismissed.size, 'class', 'classes')}.`;
  show('undo-dismiss', state.dismissHistory.length > 0);

  const ul = $<HTMLUListElement>('class-list');
  ul.replaceChildren();
  if (state.truncated) {
    ul.append(
      el('li', { class: 'callout is-warn' }, [
        'Triage is truncated: more than 200,000 findings were buffered, so only the first few hundred per class are kept verbatim. The counts are still exact.',
      ]),
    );
  }

  list.forEach((c, i) => {
    const rule = RULES.find((r) => r.id === c.rule);
    const dismissed = state.dismissed.has(c.rule);
    const li = el('li', {
      class: `class-row sev-${c.severity}${i === state.cursor ? ' is-cursor' : ''}${dismissed ? ' is-dismissed' : ''}`,
    });

    const head = el('button', { type: 'button', class: 'class-head' }, [
      el('span', { class: 'sev-tag' }, [c.severity]),
      el('span', { class: 'class-name' }, [
        rule?.label ?? c.rule,
        el('span', { class: 'class-kind' }, [
          `${c.rule} · ${c.tier === 0 ? 'checksum-validated' : c.tier === 1 ? 'published prefix' : c.tier === 2 ? 'structural' : 'heuristic'}`,
        ]),
      ]),
      el('span', { class: 'class-count' }, [
        `${plural(c.count, 'finding')} · ${formatCount(c.distinct.size)} distinct`,
      ]),
    ]);
    head.setAttribute('aria-expanded', String(state.expanded.has(c.rule)));
    head.addEventListener('click', () => {
      state.cursor = i;
      if (state.expanded.has(c.rule)) state.expanded.delete(c.rule);
      else state.expanded.add(c.rule);
      renderTriage();
    });

    const actions = el('div', { class: 'class-actions' }, []);
    const btn = el('button', { type: 'button', class: 'pill' }, [dismissed ? 'Restore' : 'Dismiss class']);
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      state.cursor = i;
      if (dismissed) restoreClass(c.rule);
      else confirmDismiss(c);
    });
    actions.append(btn);
    head.append(actions);
    li.append(head);

    if (state.expanded.has(c.rule)) {
      const body = el('div', { class: 'class-body' }, []);
      if (rule) {
        body.append(el('p', { class: 'panel-note' }, [`Does not match: ${rule.doesNotMatch}`]));
      }
      const samples = el('ul', { class: 'sample-list' }, []);
      for (const f of c.samples.slice(0, 12)) {
        samples.append(
          el('li', {}, [
            el('span', { class: 'loc' }, [
              `record ${formatCount(f.record)} · ${f.pointer || '(root)'}${f.validator ? ` · ${f.validator}` : ''}${f.note ? ` · ${f.note}` : ''}`,
            ]),
            f.rawContext,
          ]),
        );
      }
      if (c.samples.length === 0) samples.append(el('li', {}, ['(no verbatim samples retained)']));
      body.append(samples);
      li.append(body);
    }

    ul.append(li);
  });
}

function confirmDismiss(c: ClassAgg): void {
  const rule = RULES.find((r) => r.id === c.rule);
  const body = el('div', {}, []);
  body.append(
    el('p', {}, [
      'They will not be redacted. Your cleaned file will contain them exactly as they are now.',
    ]),
    el('div', { class: 'callout' }, [
      `${plural(c.count, 'finding')} · ${plural(c.distinct.size, 'distinct value')} · severity ${c.severity}`,
    ]),
  );

  if (c.tier === 1) {
    body.append(
      el('div', { class: 'callout is-warn' }, [
        el('strong', {}, [
          c.count === 1
            ? 'This matches an exact provider prefix. '
            : `All ${formatCount(c.count)} of these match an exact provider prefix. `,
        ]),
        c.count === 1 ? 'That is almost never a false positive.' : 'Those are almost never false positives.',
      ]),
    );
  }
  if (c.tier === 0) {
    body.append(
      el('div', { class: 'callout' }, [
        el('strong', {}, ['These passed a checksum. ']),
        'A checksum rules out a typo, not a coincidence — roughly one in ten random nine-digit numbers passes the routing check, and US Social Security numbers have had no checksum at all since 2011. Order ids and timestamps do turn up here.',
      ]),
    );
  }

  body.append(el('h3', {}, ['A sample of what you are dismissing']));
  const samples = el('ul', { class: 'sample-list' }, []);
  const shuffled = [...c.samples].sort(() => Math.random() - 0.5).slice(0, 5);
  for (const f of shuffled) {
    samples.append(
      el('li', {}, [
        el('span', { class: 'loc' }, [`record ${formatCount(f.record)} · ${f.pointer || '(root)'}`]),
        f.rawContext,
      ]),
    );
  }
  body.append(samples);
  if (rule) body.append(el('p', { class: 'panel-note' }, [`Does not match: ${rule.doesNotMatch}`]));

  const cancel = el('button', { type: 'button', class: 'btn' }, ['Cancel']);
  cancel.addEventListener('click', () => modal.close());
  const go = el('button', { type: 'button', class: 'btn btn-danger' }, [
    `Dismiss ${plural(c.count, 'finding')}`,
  ]);
  go.addEventListener('click', () => {
    state.dismissed.add(c.rule);
    state.dismissHistory.push(c.rule);
    log.emit('warn', `Dismissed ${plural(c.count, 'finding')} in ${c.rule} — they will NOT be redacted.`);
    modal.close();
    renderTriage();
  });
  body.append(el('div', { class: 'modal-actions' }, [cancel, go]));

  modal.show(
    c.count === 1
      ? `Dismiss the one ${c.rule} finding?`
      : `Dismiss all ${formatCount(c.count)} ${c.rule} findings?`,
    body,
  );
}

function restoreClass(rule: string): void {
  state.dismissed.delete(rule);
  state.dismissHistory = state.dismissHistory.filter((r) => r !== rule);
  log.emit('info', `Restored ${rule} — it will be redacted again.`);
  renderTriage();
}

function undoDismiss(): void {
  const last = state.dismissHistory.pop();
  if (last) restoreClass(last);
}

/* ── Export ────────────────────────────────────────────────────────────── */

async function refreshKeys(): Promise<void> {
  state.projectKey = await formatProjectKey(state.projectSecret, 'SJ1');
  state.keyFp = await keyFingerprint(state.projectSecret);
  $('projectkey-well').textContent = state.projectKey;
  $('passphrase-well').textContent = state.passphrase;
  try {
    localStorage.setItem('scrubjay.keyFp', state.keyFp);
  } catch {
    /* storage may be unavailable; the tool works without it */
  }
}

async function goExport(): Promise<void> {
  setPhase('export');
  await refreshKeys();
  const st = $('export-state');
  st.replaceChildren();
  const t = totals();
  st.append(
    el('div', { class: state.runState === 'INCOMPLETE' ? 'callout is-bad' : 'callout is-ok' }, [
      state.runState === 'INCOMPLETE'
        ? `This run did not finish. ${plural(t.active, 'finding')} were located in the ${formatBytes(state.decompressedBytes)} that was read; the rest of the file was not scanned. Artefacts are named ".partial".`
        : `${plural(t.active, 'finding')} will be redacted across ${plural(state.records, 'record')}. ${plural(state.entries.size, 'distinct entity', 'distinct entities')} will be pseudonymised.`,
    ]),
  );
  ($('dl-clean') as HTMLButtonElement).disabled = state.sinkKind === 'none';
  if (state.sinkKind === 'none') {
    st.append(
      el('div', { class: 'callout is-warn' }, [
        'The cleaned file is not available on this browser for a file this size. findings.csv, mapping.enc and report.txt all still work.',
      ]),
    );
  }
  el('p', {}, []);
  $('passphrase-well').setAttribute(
    'title',
    `${passphraseBits(state.passphrase.split('-').length).toFixed(0)} bits of entropy`,
  );
}

function partial(name: string): string {
  return state.runState === 'INCOMPLETE' ? name.replace(/(\.[^.]+)$/, '.partial$1') : name;
}

async function writeCleanFile(): Promise<void> {
  const file = state.file;
  if (!file) return;
  clearError();
  const gzip = ($('opt-gzip') as HTMLInputElement).checked;
  const base = file.name.replace(/\.(gz|parquet)$/i, '').replace(/\.(jsonl|json|ndjson|csv|txt|tsv)$/i, '');
  const suggested = partial(`${base}.clean.jsonl${gzip ? '.gz' : ''}`);

  let sink: Sink;
  try {
    // showSaveFilePicker must be reached synchronously inside the click gesture — nothing
    // awaited before it — or the browser's user-activation check rejects it.
    sink = await openSink(state.sinkKind, suggested);
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') return;
    fail(e instanceof Error ? e.message : String(e));
    return;
  }

  setPhase('scanning');
  $('progress-bar').style.width = '0%';
  abort = new AbortController();
  log.emit('info', `Pass 2 of 2 — writing ${suggested}.`);

  try {
    const result = await runPass(
      { file, pass: 2, settings: settings(), sink, gzipOutput: gzip, signal: abort.signal },
      {
        onProgress: (p) => {
          const pct = p.compressedTotal > 0 ? (p.compressedRead / p.compressedTotal) * 100 : 0;
          $('progress-bar').style.width = `${Math.min(100, pct).toFixed(1)}%`;
          $('progress-meta').replaceChildren(
            el('span', {}, [`Writing — ${formatBytes(p.compressedRead)} of ${formatBytes(p.compressedTotal)}`]),
            el('span', {}, [formatEta(p.compressedTotal - p.compressedRead, p.bytesPerSecond)]),
          );
        },
        onFindings: () => {
          /* pass 2 re-derives findings; the triage view is already built */
        },
        onEntries: ingestEntries,
        onEvent: (level, message) => log.emit(level, message),
      },
    );
    const blob = await sink.close();
    state.cleanWritten = sink.written;
    if (result.cancelled) state.runState = 'INCOMPLETE';
    if (blob) {
      state.cleanBlob = blob;
      downloadBlob(blob, suggested);
    }
    log.emit('good', `Wrote ${formatBytes(sink.written)} to ${suggested}.`);
    toast(`Cleaned file written — ${formatBytes(sink.written)}`);
    setPhase('export');
    await goExport();
  } catch (e) {
    await sink.abort();
    setPhase('export');
    state.runState = 'INCOMPLETE';
    fail(e instanceof Error ? e.message : String(e));
  }
}

function allFindings(): AppliedFinding[] {
  const out: AppliedFinding[] = [];
  for (const c of state.classes.values()) out.push(...c.samples);
  return out.sort((a, b) => a.record - b.record || a.matchStart - b.matchStart);
}

function downloadFindings(): void {
  const csv = buildFindingsCsv(allFindings(), {
    includeContext: ($('csv-context') as HTMLInputElement).checked,
    includeMatchLen: ($('csv-len') as HTMLInputElement).checked,
  });
  downloadText(csv, partial('findings.csv'), 'text/csv;charset=utf-8');
  log.emit('good', 'findings.csv downloaded. It contains no secret values — but treat it as sensitive.');
}

async function downloadMapping(): Promise<void> {
  const entries = [...state.entries.values()];
  const counts: Record<string, number> = {};
  for (const c of state.classes.values()) counts[c.rule] = c.count;
  const payload = buildMappingPayload(
    {
      source: state.file?.name ?? 'unknown',
      sourceBytes: state.file?.size ?? 0,
      projectKey: state.projectKey,
      rules: counts,
      dismissedRules: [...state.dismissed],
      normalisation: 'v1',
      runState: state.runState,
    },
    entries,
  );
  const meta: MappingMeta = {
    tool: 'scrubjay',
    v: 1,
    created: new Date().toISOString(),
    keyFp: state.keyFp,
    profile: 'SJ1',
    tokenChars: 10,
    entries: entries.length,
    containsSecrets: false,
  };
  const sealed = await sealMapping(payload, meta, state.passphrase, {
    containsSecrets: false,
    sealProjectKey: true,
  });
  downloadBytes(sealed, partial('mapping.enc'));
  log.emit('good', `mapping.enc sealed — ${formatCount(entries.length)} entries, AES-GCM-256 under your passphrase.`);
}

function downloadReport(): void {
  const counts = new Map<string, number>();
  const distinct = new Map<string, number>();
  for (const c of state.classes.values()) {
    counts.set(c.rule, c.count);
    distinct.set(c.rule, c.distinct.size);
  }
  const text = buildReport({
    fileName: state.file?.name ?? 'unknown',
    fileBytes: state.file?.size ?? 0,
    decompressedBytes: state.decompressedBytes,
    records: state.records,
    malformed: state.malformed,
    counts,
    distinct,
    dismissed: state.dismissed,
    enabledRules: state.enabled,
    runState: state.runState,
    keyFp: state.keyFp,
    entries: state.entries.size,
    startedAt: state.startedAt,
    cleanWritten: state.cleanWritten,
  });
  downloadText(text, partial('report.txt'));
  log.emit('good', 'report.txt downloaded.');
}

/* ── Mapping viewer ────────────────────────────────────────────────────── */

async function openMappingFile(file: File): Promise<void> {
  const view = $('mapping-view');
  view.replaceChildren();
  view.removeAttribute('hidden');
  const bytes = new Uint8Array(await file.arrayBuffer());

  let head: ReturnType<typeof peekMeta>;
  try {
    head = peekMeta(bytes);
  } catch (e) {
    view.append(el('div', { class: 'callout is-bad' }, [e instanceof Error ? e.message : String(e)]));
    return;
  }

  // Unauthenticated, attacker-controllable text rendered in a document that holds keys and
  // plaintext. textContent only, and labelled as unverified.
  view.append(
    el('div', { class: 'callout is-warn' }, [
      el('strong', {}, ['Unverified until you enter the passphrase. ']),
      `Created ${head.meta.created} · project ${head.meta.keyFp} · ${formatCount(head.meta.entries)} entries · profile ${head.meta.profile} · ${formatCount(head.iterations)} PBKDF2 rounds.`,
    ]),
  );

  const input = el('input', { type: 'password', placeholder: 'Passphrase' }) as HTMLInputElement;
  const go = el('button', { type: 'button', class: 'btn btn-primary' }, ['Unlock']);
  const status = el('p', { class: 'field-note' }, []);
  const table = el('div', {}, []);

  const unlock = async (): Promise<void> => {
    status.textContent = 'Deriving the key — 600,000 rounds takes a moment…';
    try {
      const opened = await openMapping(bytes, input.value);
      const lines = new TextDecoder().decode(opened.payload).split('\n').filter(Boolean);
      const rows = lines.slice(1).map((l) => JSON.parse(l) as { t: string; p: string; v: string; n: number });
      status.textContent = `Unlocked. ${formatCount(rows.length)} entries, held in this tab only.`;
      const search = el('input', { type: 'search', placeholder: 'Search a pseudonym or a value' }) as HTMLInputElement;
      const tbody = el('tbody', {}, []);
      const draw = (): void => {
        const q = search.value.trim().toLowerCase();
        tbody.replaceChildren();
        const matched = rows.filter((r) => !q || r.p.toLowerCase().includes(q) || r.v.toLowerCase().includes(q));
        for (const r of matched.slice(0, 500)) {
          tbody.append(
            el('tr', {}, [
              el('td', {}, [r.t]),
              el('td', { class: 'mono' }, [r.p]),
              el('td', { class: 'mono' }, [r.v]),
              el('td', {}, [formatCount(r.n)]),
            ]),
          );
        }
        if (matched.length > 500) {
          tbody.append(el('tr', {}, [el('td', { colspan: '4' }, [`…and ${formatCount(matched.length - 500)} more`])]));
        }
      };
      search.addEventListener('input', draw);
      const t = el('table', {}, [
        el('thead', {}, [
          el('tr', {}, [el('th', {}, ['Type']), el('th', {}, ['Pseudonym']), el('th', {}, ['Value']), el('th', {}, ['Count'])]),
        ]),
        tbody,
      ]);
      table.replaceChildren(search, el('div', { class: 'table-scroll' }, [t]));
      draw();
      log.emit('good', `Opened a mapping with ${rows.length} entries.`);
    } catch (e) {
      status.textContent = e instanceof Error ? e.message : String(e);
    }
  };

  go.addEventListener('click', () => void unlock());
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') void unlock();
  });
  view.append(el('div', { class: 'actions-row' }, [input, go]), status, table);
}

/* ── Wiring ────────────────────────────────────────────────────────────── */

/**
 * Every rule count anywhere in the interface comes from COUNTS, never from a number typed into
 * a paragraph. A hand-typed count is not a style issue — it is a safety claim the user acts on
 * by uploading four gigabytes, and prose drifts the moment a rule is added.
 */
function fillCounts(root: ParentNode): void {
  for (const node of root.querySelectorAll<HTMLElement>('[data-count]')) {
    const k = node.dataset['count'] as keyof typeof COUNTS;
    if (k in COUNTS) node.textContent = String(COUNTS[k]);
  }
}

function openTemplateModal(name: string): void {
  const tpl = document.getElementById(`tpl-${name}`) as HTMLTemplateElement | null;
  if (!tpl) return;
  const titles: Record<string, string> = {
    how: 'How it works',
    privacy: 'Privacy',
    limits: 'What Scrubjay cannot do',
    about: 'About Scrubjay',
    keys: 'Keyboard shortcuts',
  };
  const frag = tpl.content.cloneNode(true) as DocumentFragment;
  fillCounts(frag);
  modal.show(titles[name] ?? name, frag);
}

function initTheme(): void {
  const stored = (() => {
    try {
      return localStorage.getItem('scrubjay.theme');
    } catch {
      return null;
    }
  })();
  if (stored === 'light') document.documentElement.dataset['theme'] = 'light';
  $('theme-toggle').addEventListener('click', () => {
    const light = document.documentElement.dataset['theme'] === 'light';
    if (light) delete document.documentElement.dataset['theme'];
    else document.documentElement.dataset['theme'] = 'light';
    try {
      localStorage.setItem('scrubjay.theme', light ? 'dark' : 'light');
    } catch {
      /* fine */
    }
  });
}

function init(): void {
  if (!checkCapabilities()) return;

  // GitHub Pages cannot set headers and frame-ancestors is ignored in a meta policy, so this is
  // the anti-framing measure — weaker than a header, and said plainly rather than implied.
  if (self !== top) {
    document.body.replaceChildren(
      el('p', { style: 'padding:2rem;font-family:system-ui' }, [
        'Scrubjay refuses to run inside a frame. Open it at scrubjay.benrichardson.dev.',
      ]),
    );
    return;
  }

  initTheme();
  initGlossary($('tooltip'));
  createDrawer($('drawer'), $<HTMLButtonElement>('log-toggle'), $<HTMLButtonElement>('log-close'));

  $('capability-strip').replaceChildren(
    el('li', {}, [el('strong', {}, [String(COUNTS.enabled)]), ' recognisers on by default']),
    el('li', {}, [el('strong', {}, [String(COUNTS.checksum)]), ' checksum-validated']),
    el('li', {}, [el('strong', {}, [String(COUNTS.prefix)]), ' published provider prefixes']),
    el('li', {}, [el('strong', {}, [String(COUNTS.structural)]), ' structural']),
    el('li', {}, [el('strong', {}, [String(COUNTS.heuristic)]), ' heuristics, off by default']),
  );
  $('rule-count-inline').textContent = String(COUNTS.enabled);

  for (const btn of document.querySelectorAll<HTMLElement>('[data-modal]')) {
    btn.addEventListener('click', () => openTemplateModal(btn.dataset['modal']!));
  }

  const fileInput = $<HTMLInputElement>('file-input');
  $('pick-file').addEventListener('click', () => fileInput.click());
  fileInput.addEventListener('change', () => {
    const f = fileInput.files?.[0];
    if (f) void acceptFile(f);
  });

  const dz = $('dropzone');
  dz.addEventListener('dragover', (e) => {
    e.preventDefault();
    dz.classList.add('is-dragover');
  });
  dz.addEventListener('dragleave', () => dz.classList.remove('is-dragover'));
  dz.addEventListener('drop', (e) => {
    e.preventDefault();
    dz.classList.remove('is-dragover');
    const f = e.dataTransfer?.files?.[0];
    if (f) void acceptFile(f);
  });

  $('try-sample').addEventListener('click', (e) => {
    // The sample control sits inside the drop zone, so it must not also open the file picker.
    e.stopPropagation();
    void loadSample();
  });

  $('scan-start').addEventListener('click', () => void startScan());
  $('scan-back').addEventListener('click', () => setPhase('input'));
  $('scan-cancel').addEventListener('click', () => {
    abort.abort();
    log.emit('warn', 'Cancelling…');
  });
  $('go-export').addEventListener('click', () => void goExport());
  $('start-over').addEventListener('click', () => setPhase('input'));
  $('start-over-2').addEventListener('click', () => setPhase('input'));
  $('undo-dismiss').addEventListener('click', undoDismiss);
  $('retry').addEventListener('click', () => {
    clearError();
    setPhase(state.file ? 'preflight' : 'input');
  });

  $('dl-clean').addEventListener('click', () => void writeCleanFile());
  $('dl-findings').addEventListener('click', downloadFindings);
  $('dl-mapping').addEventListener('click', () => void downloadMapping());
  $('dl-report').addEventListener('click', downloadReport);

  $('copy-passphrase').addEventListener('click', () => {
    // Inside the click handler, never an async continuation — Safari and Firefox require a live
    // user gesture for clipboard writes.
    void copyText(state.passphrase).then((ok) =>
      toast(ok ? 'Passphrase copied. It is on your system clipboard now.' : 'Could not copy — select it instead.'),
    );
  });
  $('copy-projectkey').addEventListener('click', () => {
    void copyText(state.projectKey).then((ok) => toast(ok ? 'Project key copied.' : 'Could not copy.'));
  });
  $('regen-passphrase').addEventListener('click', () => {
    state.passphrase = generatePassphrase();
    $('passphrase-well').textContent = state.passphrase;
  });

  const mappingInput = $<HTMLInputElement>('mapping-input');
  $('pick-mapping').addEventListener('click', () => mappingInput.click());
  mappingInput.addEventListener('change', () => {
    const f = mappingInput.files?.[0];
    if (f) void openMappingFile(f);
  });

  $('log-copy').addEventListener('click', () => {
    void copyText(log.asText()).then((ok) => toast(ok ? 'Event log copied.' : 'Could not copy the log.'));
  });

  document.addEventListener('keydown', (e) => {
    if (modal.isOpen) return;
    const tag = (e.target as HTMLElement)?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
    if (e.key === '?') {
      openTemplateModal('keys');
      return;
    }
    if (state.phase !== 'triage' && state.phase !== 'export') return;
    const list = orderedClasses();
    if (e.key === 'J' || e.key === 'j') {
      state.cursor = Math.min(list.length - 1, state.cursor + 1);
      renderTriage();
    } else if (e.key === 'K' || e.key === 'k') {
      state.cursor = Math.max(0, state.cursor - 1);
      renderTriage();
    } else if (e.key === 'Enter') {
      const c = list[state.cursor];
      if (c) {
        if (state.expanded.has(c.rule)) state.expanded.delete(c.rule);
        else state.expanded.add(c.rule);
        renderTriage();
      }
    } else if (e.key === 'X' || e.key === 'x') {
      const c = list[state.cursor];
      if (c && !state.dismissed.has(c.rule)) confirmDismiss(c);
    } else if (e.key === 'Z' || e.key === 'z') {
      undoDismiss();
    } else if (e.key === 'E' || e.key === 'e') {
      void goExport();
    } else if (e.key === 'L' || e.key === 'l') {
      $('log-toggle').click();
    }
  });

  void refreshKeys();
  log.emit('info', `Scrubjay ready. ${COUNTS.total} recognisers loaded, ${COUNTS.enabled} on by default.`);
  log.emit('info', 'Nothing on this page sends your data anywhere. The page policy in the header is the proof.');
}

async function loadSample(): Promise<void> {
  try {
    log.emit('info', 'Fetching the bundled sample dataset…');
    const res = await fetch('/samples/support-transcripts.jsonl');
    if (!res.ok) throw new Error(`sample fetch failed (${res.status})`);
    const blob = await res.blob();
    // Wrapped in a File and fed through the EXACT same ingestion path as a real upload.
    await acceptFile(new File([blob], 'support-transcripts.jsonl', { type: 'application/x-ndjson' }));
    log.emit(
      'good',
      'Sample loaded: 2,000 synthetic support transcripts with 14 planted credentials and 30 deliberate near-misses.',
    );
  } catch (e) {
    fail(`Could not load the sample: ${e instanceof Error ? e.message : String(e)}`);
  }
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();
